// StatsStrip — compact bordered strip of label/value tiles.
//
// Lifted from the receipts list page after staff feedback that the
// six-Card KPI grid was too heavy for list-page summaries. Each tile
// is a label + value pair separated by a hairline border, all sharing
// one rounded card shell so the row stops competing with the table
// card below it. Accepts a flexible array of tiles so the same shell
// works for receipts (counters), customers (counts), payment-run
// (totals), invoices, etc.

import type { ReactNode } from 'react';
import { cn } from '../../../utils/cn';
import styles from './StatsStrip.module.css';

export interface StatsStripTile {
  /** Stable identifier — used as React key. */
  key: string;
  /** Short uppercase label rendered above the value. */
  label: ReactNode;
  /** Pre-formatted value (caller handles currency / number formatting). */
  value: ReactNode;
}

export interface StatsStripProps {
  tiles: StatsStripTile[];
  className?: string;
  ariaLabel?: string;
  variant?: 'default' | 'dark';
}

export function StatsStrip({
  tiles,
  className,
  ariaLabel,
  variant = 'default',
}: StatsStripProps): JSX.Element | null {
  if (tiles.length === 0) return null;
  return (
    <div
      className={cn(styles.strip, variant === 'dark' && styles.stripDark, className)}
      role="group"
      aria-label={ariaLabel}
    >
      {tiles.map((tile) => (
        <div
          key={tile.key}
          className={cn(styles.tile, variant === 'dark' && styles.tileDark)}
        >
          <span className={styles.label}>{tile.label}</span>
          <span className={styles.value}>{tile.value}</span>
        </div>
      ))}
    </div>
  );
}
